/**
 * Notification API Service
 * Handles all notification-related API calls
 */

import type { ApiClient } from '@commander/api-client';
import type {
  NotificationApp,
  NotificationListParams,
  NotificationListResponse,
  NotificationPreferences,
  NotificationPreferencesResponse,
} from '../types';

/**
 * Create notification API methods
 */
export function createNotificationApi(apiClient: ApiClient) {
  return {
    /**
     * Fetch notifications with pagination and filters
     */
    async getNotifications(params?: NotificationListParams): Promise<NotificationListResponse> {
      const queryParams = new URLSearchParams();

      if (params?.page != null) queryParams.append('page', params.page.toString());
      if (params?.limit != null) queryParams.append('limit', params.limit.toString());
      if (params?.is_read !== undefined) queryParams.append('is_read', params.is_read.toString());
      if (params?.is_archived !== undefined)
        queryParams.append('is_archived', params.is_archived.toString());
      if (params?.start_date) queryParams.append('start_date', params.start_date);
      if (params?.end_date) queryParams.append('end_date', params.end_date);
      if (params?.keyword) queryParams.append('keyword', params.keyword);
      if (params?.app) queryParams.append('app_type', params.app);
      if (params?.category) queryParams.append('category', params.category);

      const endpoint = `/users/notifications${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      return apiClient.get<NotificationListResponse>(endpoint);
    },

    /**
     * Fetch only the unread count for badges/indicators.
     *
     * A single COUNT on the backend — no total-count query and no page
     * payload, unlike deriving the count from `getNotifications`.
     */
    async getUnreadCount(app?: NotificationApp): Promise<number> {
      const queryParams = new URLSearchParams();
      if (app) queryParams.append('app_type', app);

      const endpoint = `/users/notifications/unread-count${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await apiClient.get<{ data: { unread_count: number } }>(endpoint);
      const count = response.data?.unread_count;
      if (typeof count !== 'number') throw new Error('Malformed unread-count response');
      return count;
    },

    /**
     * Mark a single notification as read
     */
    async markNotificationRead(notificationId: string): Promise<{ message: string }> {
      return apiClient.put<{ message: string }>(`/users/notifications/${notificationId}`, {
        is_read: true,
      });
    },

    /**
     * Mark a single notification as unread
     */
    async markNotificationUnread(notificationId: string): Promise<{ message: string }> {
      return apiClient.put<{ message: string }>(`/users/notifications/${notificationId}`, {
        is_read: false,
      });
    },

    /**
     * Mark all notifications as read
     */
    async markAllNotificationsRead(app?: string): Promise<{ message: string }> {
      const queryParams = app ? `?app_type=${app}` : '';
      return apiClient.post<{ message: string }>(
        `/users/notifications/mark_all_read${queryParams}`,
        {}
      );
    },

    /**
     * Update multiple notifications at once
     */
    async updateNotifications(
      ids: string[],
      updates: { is_read?: boolean; is_archived?: boolean }
    ): Promise<{ message: string }> {
      return apiClient.post<{ message: string }>('/users/notifications/update', {
        ids,
        ...updates,
      });
    },

    /**
     * Archive a notification
     */
    async archiveNotification(notificationId: string): Promise<{ message: string }> {
      return apiClient.put<{ message: string }>(`/users/notifications/${notificationId}`, {
        is_archived: true,
      });
    },

    /**
     * Archive all notifications
     */
    async archiveAllNotifications(app?: string): Promise<{ message: string }> {
      const queryParams = app ? `?app_type=${app}` : '';
      return apiClient.post<{ message: string }>(
        `/users/notifications/mark_all_archived${queryParams}`,
        {}
      );
    },

    /**
     * Get notification preferences
     */
    async getPreferences(): Promise<NotificationPreferencesResponse> {
      return apiClient.get<NotificationPreferencesResponse>('/users/notification-preferences');
    },

    /**
     * Update notification preferences
     */
    async updatePreferences(
      preferences: Partial<NotificationPreferences>
    ): Promise<NotificationPreferencesResponse> {
      return apiClient.put<NotificationPreferencesResponse>(
        '/users/notification-preferences',
        preferences
      );
    },
  };
}

export type NotificationApi = ReturnType<typeof createNotificationApi>;
